/**
 * Main layout component with header and theme toggle.
 */

import { Outlet, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import OrgSwitcher from './OrgSwitcher';

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg text-content-secondary hover:bg-surface-tertiary transition-colors"
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
      title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {theme === 'light' ? (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
        </svg>
      )}
    </button>
  );
}

export default function Layout() {
  const { isAuthenticated, identity, logout, isLoading } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orbital-50 to-orbital-100 dark:from-gray-900 dark:to-gray-950">
      {/* Header */}
      <header className="bg-surface-primary border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              {isAuthenticated ? (
                <>
                  <img src="/orbital-passport-lm.svg" alt="OrbitalPassport" className="h-9 w-9 dark:hidden" />
                  <img src="/orbital-passport-transparent.svg" alt="OrbitalPassport" className="h-9 w-9 hidden dark:block" />
                </>
              ) : (
                <>
                  <img src="/orbital-passport-logo-lm.svg" alt="OrbitalPassport" className="h-10 dark:hidden" />
                  <img src="/orbital-passport-logo-dark.svg" alt="OrbitalPassport" className="h-10 hidden dark:block" />
                </>
              )}
            </Link>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              <ThemeToggle />

              {!isLoading && isAuthenticated && (
                <>
                  <div className="flex items-center space-x-3">
                    <OrgSwitcher />
                    <span className="text-sm text-content-secondary">
                      {identity?.traits?.email as string || 'User'}
                    </span>
                    <Link
                      to="/devices"
                      className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300"
                    >
                      Devices
                    </Link>
                    <Link
                      to="/agent-tokens"
                      className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300"
                    >
                      Agent Tokens
                    </Link>
                    <Link
                      to="/settings"
                      className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300"
                    >
                      Settings
                    </Link>
                    <button
                      onClick={logout}
                      className="text-sm text-content-secondary hover:text-content-primary"
                    >
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-surface-primary border-t border-border mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-4 text-center text-sm text-content-tertiary">
          OrbitalPassport - Centralized Identity Management
        </div>
      </footer>
    </div>
  );
}
